<template>
  <div class="container">
    <div class="content" v-if="this.data">
      <!-- This code creates a div that displays information about a booking -->
      <div class="title">
        <div class="info">
          <!-- This line displays the passenger's full name -->
          <p class="order-id">
            <span class="material-icons middle">person</span>
            <span class="middle" v-if=" data.origin !== 'phone'">
              {{ data.passenger_name }}
            </span>
            <span class="middle" v-else>
              {{ data.meta_passenger_name }}
            </span>
            <!-- This line displays the booking ID -->
            <span :style="{
              color: data.high_priority == 1 ? 'rgb(239 194 11)' : '#e10015',
              fontWeight: 'bold',
              border: data.high_priority == 1 ? '1px solid rgb(239 194 11)' : '1px solid #e10015',
              padding: '3px'
            }">{{ data.id }}</span>

            <!-- This line displays a mobile icon if the booking from customer app -->
            <span style="color: #788e98; float: inline-end; position: absolute; top: 0; inset-inline-end: 0"
              v-if="data.origin === 'app'">
              <v-icon style="color: #27a432"> fa fa-mobile </v-icon>
            </span>
            <!-- This line displays a laptop icon sif the booking from call center -->
            <span style="color: #788e98; float: inline-end; position: absolute; top: 0; inset-inline-end: 0"
              v-else-if="data.origin === 'dashboard'">
              <v-icon style="color: #27a432"> fa fa-laptop </v-icon>
            </span>
            <!-- This line displays a phone icon if the booking from customer dashboard -->
            <span style="color: #788e98; float: inline-end; position: absolute; top: 0; inset-inline-end: 0"
              v-else-if="data.origin === 'phone'">
              <v-icon style="color: #27a432"> fa fa-phone </v-icon>
            </span>
          </p>
          <p class="datetime" v-if="data.passenger_username && data.origin !== 'phone'">
            <span class="material-icons middle">local_phone</span>
            <span>
              {{
                data.passenger_username
              }}</span>
          </p>

          <p class="datetime" v-if="data.meta_passenger_phone_number && data.origin == 'phone'">
            <span class="material-icons middle">local_phone</span>
            <span>
              {{
                data.meta_passenger_phone_number
              }}</span>
          </p>

        </div>
      </div>

      <p v-if="data.status === 'PAID'" style="color: green"><b>{{ $t("orderCardX.completed") }}</b></p>
      <p v-else-if="data.status === 'PASSENGER_CANCELLED'" style="color: red">
        <b>{{ $t("orderCardX.cancelledByCustomer") }}</b>
      </p>
      <p v-else-if="data.status === 'CAPTAIN_CANCELLED'" style="color: red">
        <b>{{ $t("orderCardX.cancelledByDriver") }}</b>
      </p>
      <p v-else-if="data.status === 'SYSTEM_CANCELLED'" style="color: red">
        <b>{{ $t("orderCardX.cancelledBySystem") }}</b>
      </p>
      <p v-else-if="data.status === 'CC_CANCELLED'" style="color: red">
        <b>{{ $t("orderCardX.cancelledByCc") }}</b>
      </p>
      <p v-else-if="show_status">
        {{ this.orderStatuses[data.status] }}
      </p>

      <!-- S1: why is this order still waiting - dispatch state from the API.
           One line only; absent on older payloads and on non-pending cards. -->
      <p class="datetime" v-if="dispatchState"
        :style="{ color: dispatchState.warning ? '#e65100' : '#546e7a' }">
        <span class="material-icons middle">{{ dispatchState.warning ? 'report_problem' : 'sync' }}</span>
        <span class="middle">{{ dispatchState.text }}</span>
      </p>

      <div class="locations">
        <div class="location">
          <div class="circle" style="background-color: #fff"></div>
          <label class="location">
            <label style="color: #00abb3">{{ data.pickup_name }}, </label>

          </label>
        </div>
        <div class="location">
          <div class="circle"></div>
          <label>{{ data.dropdown_name }}</label>
        </div>
      </div>

      <p class="datetime" v-if="(data.service_name)">
        <span class="material-icons middle">miscellaneous_services</span>
        <span class="middle">{{ data.service_name }}</span>

      </p>

      <p class="datetime" v-if="data.price">
        <span class="material-icons middle">attach_money</span>
        <span class="middle">{{ formatPrice(data.price) }}</span>
      </p>

      <p class="datetime" v-if="data.estimated_price">
        <span class="material-icons middle">attach_money</span>
        <span class="middle">
          {{ $t("orderCardX.estimated") }}: {{ formatPrice(data.estimated_price) }}
        </span>
      </p>

      <p class="datetime" v-if="fareFallbackText" style="color: #e65100">
        <span class="material-icons middle">report_problem</span>
        <span class="middle">{{ fareFallbackText }}</span>
      </p>

      <!-- U06: discount / voucher used on this order -->
      <p class="datetime" v-if="data.discount_amount || data.voucher_code">
        <span class="material-icons middle">local_offer</span>
        <span class="middle">
          {{ $t('orderCard.discount') }}:
          {{ data.discount_amount ? formatPrice(data.discount_amount) : '-' }}
          <span v-if="data.voucher_code"> ({{ data.voucher_code }})</span>
        </span>
      </p>

      <!-- U05: customer cancellation reason -->
      <p class="datetime" v-if="data.cancellation_reason" style="color: #e53935">
        <span class="material-icons middle">cancel</span>
        <span class="middle">
          {{ $t('orderCard.cancellationReason') }}: {{ data.cancellation_reason }}
        </span>
      </p>

      <!-- C4: electronic payment details (method + amount + merchant account + txn) -->
      <p class="datetime" v-if="data.online_payment" style="color: #2e7d32">
        <span class="material-icons middle">payment</span>
        <span class="middle">
          {{ $t('orderCard.paidOnline') }}:
          {{ paymentMethodName(data.online_payment) }}
          - {{ formatPrice(data.online_payment.amount) }}
          <span v-if="data.online_payment.merchant_account">
            - {{ $t('orderCard.merchantAccount') }}: {{ data.online_payment.merchant_account }}
          </span>
          <span v-if="data.online_payment.transaction_number">
            - {{ $t('orderCard.transaction') }}: {{ data.online_payment.transaction_number }}
          </span>
        </span>
      </p>

      <!-- WALLET: ride settled from the rider's in-app wallet (full or split) -->
      <p class="datetime" v-if="data.wallet_payment" style="color: #6a5acd">
        <span class="material-icons middle">account_balance_wallet</span>
        <span class="middle">
          {{ $t('orderCard.paidFromWallet') }}
          <span v-if="paymentMethodName(data.wallet_payment)">({{ paymentMethodName(data.wallet_payment) }})</span>
          - {{ formatPrice(data.wallet_payment.amount) }}
          <span v-if="data.wallet_payment.balance_after != null">
            - {{ $t('orderCard.walletRemaining') }}: {{ formatPrice(data.wallet_payment.balance_after) }}
          </span>
          <span v-if="data.wallet_payment.date"> - {{ data.wallet_payment.date }}</span>
        </span>
      </p>

      <!-- B2B: order charged to a business account -->
      <p class="datetime" v-if="data.payer === 'BUSINESS'" style="color: #1565c0">
        <span class="material-icons middle">business</span>
        <span class="middle">
          {{ $t('orderCard.chargedToBusiness') }}<span v-if="data.business_account_name">: {{ data.business_account_name }}</span>
        </span>
      </p>

      <p class="datetime" v-if="data.rate_order !== null && data.rate_order !== undefined">
        <span class="material-icons middle">star</span>
        <span class="middle" style="display: inline-flex; align-items: center; gap: 1px;">
          <span 
            v-for="n in 5" 
            :key="n"
            class="material-icons"
            :style="{ 
              color: n <= data.rate_order ? '#f3c220' : (n - 0.5 <= data.rate_order ? '#f3c220' : '#e0e0e0'),
              fontSize: '18px',
              opacity: n <= data.rate_order ? '1' : (n - 0.5 <= data.rate_order ? '0.5' : '0.3')
            }"
          >
            {{ n <= data.rate_order ? 'star' : (n - 0.5 <= data.rate_order ? 'star_half' : 'star_border') }}
          </span>
        </span>
      </p>

      <p class="datetime" v-if="(data.car_brand_name)">
        <span class="material-icons middle">directions_car</span>
        <span class="middle" v-if="data.car_brand_model && data.car_brand_name"> {{ data.car_brand_name }} ,
          {{ data.car_brand_model }}, {{ data.car_color }}</span>

      </p>
      <div v-if="
        data.taxi_number
      " class="w-full float-left">
        <div class="driver">
          <div class="captain-profile-pic">
            0<span v-if="data.taxi_number">{{ data.taxi_number }}</span>
          </div>
          <label>

            <span class="material-icons middle" v-if="data.captain_first_name">person_pin</span>
            <span class="middle" v-if="data.captain_first_name">{{ data.captain_first_name }}</span>

          </label>
        </div>
      </div>
      <p class="datetime" v-if="(data.car_plate_number)">
        <span class="material-icons middle">credit_card</span>
        <span class="middle">{{ data.car_plate_number }}</span>
      </p>






      <p class="datetime">
        <span class="material-icons middle">schedule</span>
        <span class="middle">{{ formateDateTime(data.created_at) }}</span>
      </p>


      <p class="datetime" v-if="!data.now">
        <span class="material-icons middle">departure_board</span>
        <span class="middle">{{ formateDateTime(data.datetime) }}</span>
      </p>

      <div v-if="actions && !hideActions" class="actions-container">
        <div class="actions">
          <button v-for="(action, index) in actions" :key="index">
            <img :src="require(`@/assets/actions/${action.img}`)" @click="handleFunctionCall(action.method)"
              :title="action.hint" />
          </button>


        </div>
      </div>
    </div>
    <div v-else>{{ $t("orderCardX.loading") }}</div>
    <v-dialog v-model="dialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline"> {{ $t("orderCardX.chooseDriverTitle") }} </span>
        </v-card-title>
        <v-card-text>
          <v-autocomplete v-model="returnObject" clearable :items="captains" :loading="isLoading"
            :search-input.sync="search" outline dense :label="$t('orderCardX.driver')" item-text="text" item-value="text"
            :placeholder="$t('orderCardX.searchDriver')" no-filter return-object>
            <template slot="item" slot-scope="captains">
              <v-list-tile-content>
                <v-list-tile-title
                  v-html="captains.item.first_name + ' ' + captains.item.last_name"></v-list-tile-title>
                <v-list-tile-sub-title v-html="captains.item.username"></v-list-tile-sub-title>
              </v-list-tile-content>
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" flat @click="dialog = false">{{ $t("orderCardX.close") }}</v-btn>
          <v-btn color="primary" :loading="loading" flat @click="manualDispatch">{{ $t("orderCardX.dispatch") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancellationDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline"> {{ $t("orderCardX.cancellationReasonTitle") }} </span>
        </v-card-title>
        <v-card-text>
          <div v-for="option in userCancellationReasons" :key="option.id" class="radio-option">
            <input type="radio" :value="option.id" v-model="returnUserCancellationReason" :id="option.id"
              class="radio-input" />
            <label :for="option.id" class="radio-label">{{ cancellationReasonText(option) }}</label>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" flat @click="cancellationDialog = false">{{ $t("orderCardX.close") }}</v-btn>
          <v-btn color="primary" :loading="loading" flat @click="submitCancelOrder">{{ $t("orderCardX.submit") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import {
  companyFromNow,
  companyNow,
  formatCompanyTime,
  toCompanyMoment,
} from "@/util/companyTime";
import api from "@/constants/api.js";
let CancelToken = axios.CancelToken;
let cancel;

export default {
  name: "OrderCard",
  props: {
    data: {
      type: Object,
      required: false,
    },
    id: {
      type: Number,
      required: false,
    },
    hideActions: {
      type: Boolean,
      required: false,
      default: false,
    },

    show_status: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      userCancellationReasons: [],
      constraints: [],

      edit: 0,
      pickup: "Malki",
      destination: "Damascus",
      loading: false,
      showPastTime: true,
      date: "",
      test: "",
      diff: "",
      red: false,
      dialog: false,
      cancellationDialog: false,
      returnObject: null,
      returnUserCancellationReason: "",
      isLoading: false,
      search: null,
      captains: [],
      currencyFormatter: new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "SYP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
      formatters: {
        // Final-version doc 2026-10-05: company clock (UTC+3), not the PC's zone.
        datetime(value) {
          return formatCompanyTime(value, "HH[h]m - DD/MM/YYYY");
        },
        created_at(value) {
          return formatCompanyTime(value, "HH[h]m - DD/MM/YYYY");
        },
      },
      currencyFormatter: new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "SYP",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    };
  },

  computed: {
    time: function () {
      return moment(this.date);
    },
    // Fare question 1303: the GPS track of this ride was cut short, so the
    // backend billed a fallback distance instead, or management corrected
    // the fare afterwards (meters.distance_source).
    fareFallbackText() {
      const d = this.data || {};
      const keys = {
        estimate_short_track: "orderCardX.fareShortTrackEstimate",
        straight_short_track: "orderCardX.fareShortTrackStraight",
        manual_correction: "orderCardX.fareManualCorrection",
      };
      const key = keys[d.distance_source];
      if (!key) return "";
      const km = (v) => (v == null || isNaN(Number(v)) ? "-" : Number(v).toFixed(1));
      return this.$t(key, { billed: km(d.billed_km), gps: km(d.gps_km) });
    },
    orderStatuses() {
      return {
        PENDING: this.$t("orderCardX.statusPending"),
        ACCEPTED: this.$t("orderCardX.statusAccepted"),
        STARTED: this.$t("orderCardX.statusStarted"),
        ARRIVED: this.$t("orderCardX.statusArrived"),
        PICKED_UP: this.$t("orderCardX.statusPickedUp"),
        DONE: this.$t("orderCardX.statusDone"),
        PAID: this.$t("orderCardX.statusPaid"),
        CAPTAIN_CANCELLED: this.$t("orderCardX.statusCaptainCancelled"),
        PASSENGER_CANCELLED: this.$t("orderCardX.statusPassengerCancelled"),
        SYSTEM_CANCELLED: this.$t("orderCardX.statusSystemCancelled"),
        CC_CANCELLED: this.$t("orderCardX.statusCcCancelled"),
      };
    },
    // S1: tell the operator WHY a pending order is still pending. The backend
    // attaches `dispatch_state` only to PENDING bookings and only on recent
    // builds, so every access is guarded and anything unexpected renders
    // nothing rather than an empty or broken line.
    dispatchState() {
      if (!this.data || this.data.status !== "PENDING") return null;
      const state = this.data.dispatch_state;
      if (!state || typeof state !== "object") return null;
      // The order WAS aimed at one specific captain and the backend has since
      // reopened it to the pool (TARGET_CAPTAIN_FALLBACK). The release NULLs
      // target_captain_id, so released_target_captain_id is the only field
      // left that still carries that history.
      const released = !!(state.released || state.released_target_captain_id);
      // A LIVE offer always wins over no_candidates. no_candidates is derived
      // from booking_metadata.finish_dispatching_at, which the backend stamps
      // on any empty or failed dispatch pass and never clears, so it stays
      // true even after a later pass DID find a captain. Reading it first
      // would tell the operator to give up on an order whose offer window is
      // open on a captain's phone right now.
      if (state.last_offer_status === "OFFERED")
        return {
          warning: false,
          text: released
            ? this.$t("orderCardX.dispatchReleasedTargetOffering")
            : this.$t("orderCardX.dispatchOffering"),
        };
      if (state.no_candidates) {
        // Still directed at one captain: TARGET_CAPTAIN_FALLBACK is off, or
        // the release itself failed.
        if (state.target_captain_id)
          return {
            warning: true,
            text: this.$t("orderCardX.dispatchNoDriverTargetRejected"),
          };
        if (released)
          return {
            warning: true,
            text: this.$t("orderCardX.dispatchReleasedTargetNoDriver"),
          };
        const rejected = Number(state.rejected_count) || 0;
        if (rejected > 0)
          return {
            warning: true,
            text: this.$t("orderCardX.dispatchNoDriverRejected", { count: rejected }),
          };
        return { warning: true, text: this.$t("orderCardX.dispatchNoDriver") };
      }
      return null;
    },
    actions() {
      if (!this.data) return [];
      const actions = [];
      // Details icon (was commented out before)
      if (this.$route && this.$route.name !== "Bookings") {
        actions.push({
          img: "openMap.svg",
          hint: this.$t("orderCardX.openDetailsHint"),
          method: "openDetails",
        });
      }
      if (
        ["PENDING", "ACCEPTED", "STARTED", "ARRIVED"].includes(
          this.data.status
        )
      ) {
        actions.push({
          img: "cancel.svg",
          hint: this.$t("orderCardX.cancelOrderHint"),
          method: "cancelOrder",
        });
      }
      if (this.data.status == "PENDING") {
        actions.push({
          img: "dispatch.svg",
          hint: this.$t("orderCardX.dispatchHint"),
          method: "dispatchPopup",
        });
      }
      actions.push({
        img: "info.svg",
        hint: this.$t("orderCardX.orderLogsHint"),
        method: "orderLogs",
      });
      return actions;
    },
  },
  watch: {
    diff() {
      this.test = this.diff;
      if (this.diff < 600) {
        this.red = true;
      }
    },

    search(val) {
      this.isLoading = true;
      if (cancel) cancel();
      const params = new URLSearchParams(this.initialSearchParams);
      params.append("search", val);
      this.axios
        .get("captain/search", {
          params: params,
          cancelToken: new CancelToken(function executor(c) {
            cancel = c; // c is the cancel function
          }),
        })
        .then((res) => {
          this.captains = res.data.data.map((item) => ({
            text: `${item.full_name || ""}` + " " + `${item.username || ""}`,
            ...item,
          }));
        })
        .catch((err) => console.log(err))
        .finally(() => (this.isLoading = false));
    },
  },
  mounted: function () {
    if (this.diff)
      setInterval(() => {
        this.diff--;
      }, 1000);
  },
  created() {
    if (this.data) {
      this.showPastTime = ["PENDING", "ACCEPTED"].includes(this.data.status);
    }
  },
  methods: {
    formatPrice(value) {
      if (value == null) return "";
      return this.formatMoney(value) + " " + this.$t("orderCardX.currency");
    },
    paymentMethodName(op) {
      if (!op) return "";
      const isAr = this.$i18n && this.$i18n.locale === "ar";
      return (isAr ? op.method_ar : op.method_en) || op.method_en || op.method_ar || "";
    },
    cancellationReasonText(option) {
      if (!option) return "";
      const isAr = this.$i18n && this.$i18n.locale === "ar";
      return (isAr ? option.text_ar : option.text_en) || option.text_en || option.text_ar || "";
    },
    validateEmail(email) {
      let res = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
      return res.test(email);
    },
    sendPendingReminder() {
      // Final-version doc 2026-10-05: read the server's Damascus wall time as
      // a real instant and compare with the server-synced clock, so a PC in
      // another zone does not shift the 15-minute window by its offset.
      let orderDate = toCompanyMoment(this.data.created_at);
      if (orderDate && orderDate.isAfter(companyNow().subtract(15, "minutes"))) {
        window.getApp.$emit("SHOW_TOAST", this.$t("orderCardX.pendingReminderTooSoon"), "red");
        return;
      }


      this.$dialova({
        title: this.$t("orderCardX.sendReminder"),
        paragraph: this.$t("orderCardX.confirmReminderDrivers"),
        loader: true,
      })
        .then(({ close }) => {
          this.axios
            .post(`send_pending_reminder`, { booking_id: this.data.id })
            .then(() => {
              window.getApp.$emit(
                "SHOW_TOAST",
                this.$t("orderCardX.reminderSent"),
                "green"
              );

              close();
            })
            .catch((err) => {
              window.getApp.$emit("SHOW_TOAST", err.response.data.message || this.$t("orderCardX.errorOccurred"), "red");
              close();
            });
        })
        .catch(() => {
          console.log("cancelled");
        });
    },
    sendDispatchedReminder() {
      let orderDate = toCompanyMoment(this.data.datetime);
      if (orderDate && orderDate.isAfter(companyNow())) {
        window.getApp.$emit("SHOW_TOAST", this.$t("orderCardX.dispatchedReminderTooSoon"), "red");
        return;
      }


      this.$dialova({
        title: this.$t("orderCardX.sendReminder"),
        paragraph: this.$t("orderCardX.confirmReminderDriver"),
        loader: true,
      })
        .then(({ close }) => {
          this.axios
            .post(`send_dispatched_reminder`, { booking_id: this.data.id })
            .then(() => {
              window.getApp.$emit(
                "SHOW_TOAST",
                this.$t("orderCardX.reminderSent"),
                "green"
              );

              close();
            })
            .catch((err) => {
              window.getApp.$emit("SHOW_TOAST", err.response.data.message || this.$t("orderCardX.errorOccurred"), "red");
              close();
            });
        })
        .catch(() => {
          console.log("cancelled");
        });
    },
    editorder() {
      this.edit = 1;
      const routeData = this.$router.resolve({
        name: "PhoneBooking",
        query: {
          show_order_list: false,
          id: this.data.id,
          grouper: this.data.group_order,
          // date: this.data.datetime,
          edit: 1,
        },
      });
      window.open(routeData.href, "_blank");
    },
    dispatchPopup() {
      this.dialog = true;
    },
    goToChat() {
      this.$router.push('/messages?conversation=' + this.data.ride.captain.conversation_id);
    },
    manualDispatch() {
      if (!this.returnObject)
        return;
      this.loading = true;
      this.axios
        .put(`dispatch/${this.data.id}/${this.returnObject.id}`)
        .then(() => {
          window.getApp.$emit(
            "SHOW_TOAST",
            this.$t("orderCardX.orderDispatched", { id: this.data.id }),
            "green"
          );

          this.dialog = false;
        })
        .catch((err) => {
          window.getApp.$emit("SHOW_TOAST", err.response.data.message, "red");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    orderLogs() {
      this.$router.push({
        name: "BookingLog",
        params: { id: this.data.id },
      });
    },
    openMap() {
      this.$router.push({
        name: "Details",
        params: {
          data: JSON.stringify(this.data),
          id: this.data.id,
        },
      });
    },
    handleFunctionCall(func) {
      this[func]();
    },
    formateDateTime(time) {
      return formatCompanyTime(time, " DD[/]MM[/]YYYY HH:m");
    },
    pastTime(time) {
      moment.locale("fr");
      return companyFromNow(time);
    },
    cancelOrder() {
      this.$store
        .dispatch("getUserCancellationReasons")
        .then((data) => {
          this.userCancellationReasons = data;
          this.cancellationDialog = true;
        })
        .catch((err) => {
          console.error(err);
        });
      // this.$dialova({
      //   title: `Cancel order #${this.data.id}`,
      //   paragraph: `Are you sure you want to cancel <b>order #${this.data.id
      //     }</b> for passenger <b>${this.data.passenger && this.data.passenger.full_name
      //       ? this.data.passenger.full_name
      //       : ""
      //     }</b>${this.data.captain_name
      //       ? "that is dispatched with " + this.data.captain_name
      //       : ". order is not dispatched yet."
      //     }`,
      //   loader: true,
      // }).then(({ close }) => {
      //   axios
      //     .delete(api.ACTIONS.BOOKINGS + `/${this.data.id}`)
      //     .then(() => {
      //       window.getApp.$emit(
      //         "SHOW_TOAST",
      //         `Order #${this.data.id} has been cancelled`,
      //         "green"
      //       );
      //       console.log("emitted", this.data.id);
      //       this.$emit("cancelled", this.data.id);
      //     })
      //     .catch((err) => {
      //       window.getApp.$emit("SHOW_TOAST", err.response.data.message, "red");
      //     })
      //     .finally(() => {
      //       close();
      //     });
      // });
    },
    submitCancelOrder() {
      if (!this.returnUserCancellationReason) {
        window.getApp.$emit("SHOW_TOAST", this.$t("orderCardX.chooseReason"), "red");
        return;
      }
      this.loading = true;

      axios
        .delete(api.ACTIONS.BOOKINGS + `/${this.data.id}`, {
          params: {
            user_cancellation_reason_id: this.returnUserCancellationReason,
          },
        })
        .then(() => {
          window.getApp.$emit(
            "SHOW_TOAST",
            this.$t("orderCardX.orderCancelled", { id: this.data.id }),
            "green"
          );
          console.log("emitted", this.data.id);
          this.$emit("cancelled", this.data.id);
        })
        .catch((err) => {
          window.getApp.$emit("SHOW_TOAST", err.response.data.message, "red");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    stringToColour(str) {
      var hash = 0;
      for (var i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      var colour = "#";
      for (var i = 0; i < 3; i++) {
        var value = (hash >> (i * 8)) & 0xff;
        colour += ("00" + value.toString(16)).substr(-2);
      }
      return colour;
    },
    openDetails() {
      // Go to /rides/:id (RidePage details)
      if (!this.data || !this.data.id) return;
      this.$router.push({ name: "Bookings", params: { id: this.data.id } });
    },
    rescheduleOrder() {
      this.$dialova({
        title: this.$t("orderCardX.changeTimeTitle", { id: this.data.id }),
        paragraph: this.$t("orderCardX.changeTimeParagraph", {
          id: this.data.id,
          passenger: this.data.passenger.full_name,
          from: this.data.pick_up_location_details,
          to: this.data.pull_down_location_details || this.data.pull_down_location_name,
          time: this.formateDateTime(this.data.datetime),
        }),
        loader: true,
        input: this.$t("orderCardX.enterNewDatetime"),
        type: "datetime-local",
      }).then(({ close, value }) => {
        axios
          .put(`booking/${this.data.id}`, {
            datetime: `${moment(value).format("YYYY-MM-DD HH:mm")}`,
          })
          .then((res) => {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.$t("orderCardX.timeChanged", {
                id: this.data.id,
                from: this.formateDateTime(this.data.datetime),
                to: this.formateDateTime(value),
              }),
              "green"
            );
            this.data.datetime = res.data.data.datetime;
          })
          .catch((err) => {
            console.log("ERROR");
            console.log(err.response);
            window.getApp.$emit("SHOW_TOAST", err.response.data.message, "red");
          })
          .finally(() => {
            close();
          });
        console.log(value);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.timer {
  color: red;
  font-weight: bold;
}

.strike_through {
  text-decoration-line: line-through
}

.radio-container {
  max-width: 400px;
  margin: 0 auto;
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 8px;
  background-color: #f9f9f9;
}

.radio-option {
  padding-inline-start: 30px;
  display: flex;
  align-items: center;
  margin: 10px 0;
  font-family: 'Poppins';
}

.radio-input {
  margin-inline-end: 10px;
  cursor: pointer;
  accent-color: #4caf50;
  /* Change color of the radio button in modern browsers */
}

.radio-label {
  font-size: 16px;
  cursor: pointer;
}

.container {
  /*w-full h-auto float-left rounded-lg bg-white px-5 py-4 shadow*/
  width: 100%;
  height: auto;
  float: inline-start;
  margin: 0;
  border-radius: 8px;
  background: white;
  padding: 1rem 1.25rem;
  box-shadow: 0px 0px 6px rgba($color: #000000, $alpha: 0.08);

  &.priority-border {
    box-shadow: inset 0px 0px 0px 1px gold, 0px 0px 6px rgba($color: gold, $alpha: 0.08);
  }

  p {
    margin: 0px;
  }

  .content {
    /*w-full float-left flex flex-col gap-5 font-medium*/
    width: 100%;
    float: inline-start;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
    font-weight: 500;

    /*w-full float-left*/
    .title {
      width: 100%;
      float: inline-start;
      line-height: auto !important;
      font-family: Poppins !important;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .info {
        float: inline-start;
        color: #222629;
        background: transparent !important;
        font-size: 14px;
        font-weight: normal;
        width: 100%;
        position: relative;

        .order-id {
          // width: 100%;
          float: inline-start;
          display: flex;
          flex-wrap: wrap;
          width: 80%;
          align-items: center;

          p {
            width: 100%;
            float: inline-start;
          }

          span {
            margin: 0 2%;
          }
        }
      }

      .constraints {
        float: inline-end;
        height: auto;
        display: flex;
        flex-direction: row;
        gap: 7px;

        img {
          float: inline-end;
        }
      }
    }

    .locations {
      width: 100%;
      float: inline-start;
      height: auto;
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: 12px;

      // w-full flex flex-row items-center
      .location {
        // width: 100%;
        display: flex;
        flex-direction: row;
        align-items: center;
        float: inline-start;

        .circle {
          border-radius: 100%;
          width: 15px;
          min-width: 15px;
          height: 15px;
          min-height: 15px;
          float: inline-start;
          background: #e10015;
          border: 3px solid #e10015;
        }

        label {
          margin-inline-start: 4px;
          float: inline-start;
          word-break: break-word;
        }
      }
    }

    /*w-full float-left text-sm text-gray*/
    .datetime {
      width: 100%;
      float: inline-start;
      font-size: 14px;
      color: #788e98;
      font-weight: 400;
    }

    // w-full float-left flex flex-row items-center gap-2
    .driver {
      width: 100%;
      float: inline-start;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.4rem;

      .captain-profile-pic {
        max-height: 24px;
        padding: 2px 8px;
        background-color: #e1e1e1;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 99px;
      }


      // rounded-full overflow-hidden img-shadow
      img {
        border-radius: 100%;
        overflow: hidden;
      }
    }

    // w-full float-left
    .actions-container {
      width: 100%;
      float: inline-start;

      // w-full float-left mt-4 flex flex-row gap-2
      .actions {
        width: 100%;
        float: inline-start;
        display: flex;
        flex-direction: row;
        gap: 0.5rem;

        button {
          max-height: 22px;
        }
      }
    }
  }
}

.middle {
  vertical-align: middle;
}
</style>
